import type { MouseEvent } from "react"
import { Warning, WarningCircle } from "@phosphor-icons/react"

import { Badge, Button } from "~/components/design-system/components"
import { Tooltip } from "~/components/reusable/Tooltip"
import { partiallySelectedGroupsMessage } from "~/utils/violationMessages"

/**
 * `plan`: a buy plan, MOQ across warehouses. `noosBuy`: a NOOS buy, whose MOQ uplift follows weeks
 * on hand. `order`: one purchase order, MOQ across its lines.
 */
type ResolveScope = "plan" | "noosBuy" | "order"

type ToolbarCopy = { moqLabel: string; colliLabel: string; moqTitle: string; colliTitle: string }

const PLAN_COPY: ToolbarCopy = {
  moqLabel: "Resolve all MOQ violations",
  colliLabel: "Resolve all colli size violations",
  moqTitle:
    "Raises every SKU and group below its MOQ up to the minimum, across warehouses. Quantities set to 0 stay at 0.",
  colliTitle:
    "Rounds every warehouse quantity that is not a multiple of its colli size up to the next multiple.",
}

const TOOLBAR_COPY: Record<ResolveScope, ToolbarCopy> = {
  plan: PLAN_COPY,
  noosBuy: {
    ...PLAN_COPY,
    moqTitle:
      "Raises every SKU and group below its MOQ up to the minimum, across warehouses. Extra units go to the sizes and warehouses with the fewest weeks on hand, which can include sizes at 0. A size at 0 stays at 0 when it is excluded, already on a sent PO, has its own MOQ or has no forecast. Colli size is not applied, so resolve colli size violations afterwards.",
  },
  order: {
    moqLabel: "Resolve all MOQ violations",
    colliLabel: "Resolve all colli size violations",
    moqTitle:
      "Raises every SKU and group below its MOQ up to the minimum, across the order's lines. Quantities set to 0 stay at 0.",
    colliTitle:
      "Rounds every line that is not a multiple of its colli size up to the next multiple.",
  },
}

const FILTERED_COLLI_TITLE =
  "Rounds this warehouse's quantities that are not a multiple of their colli size up to the next multiple. Other warehouses are left as they are."

// The PO editor's toolbar sits inside a form.
const handled = (action: () => void) => (e: MouseEvent) => {
  e.preventDefault()
  e.stopPropagation()
  action()
}

type ToolbarProps = {
  scope: ResolveScope
  /** Variants with a violation each button would fix. */
  moqCount: number
  colliCount: number
  moqLockedReason?: string
  warehouseFiltered?: boolean
  disabled?: boolean
  onResolveMoq: () => void
  onResolveColli: () => void
}

/** Table-toolbar buttons resolving every MOQ / colli size violation, with variant-count badges. */
export function ResolveViolationsToolbar({
  scope,
  moqCount,
  colliCount,
  moqLockedReason,
  warehouseFiltered = false,
  disabled = false,
  onResolveMoq,
  onResolveColli,
}: ToolbarProps) {
  const copy = TOOLBAR_COPY[scope]
  return (
    <>
      <Button
        type="button"
        requires="edit"
        variant="neutral"
        size="sm"
        allowedTitle={moqLockedReason ?? copy.moqTitle}
        disabled={disabled || !!moqLockedReason || moqCount === 0}
        onClick={handled(onResolveMoq)}
      >
        <Warning weight="fill" />
        {copy.moqLabel}
        {moqCount > 0 && (
          <Badge size="xs" variant="reorder">
            {moqCount}
          </Badge>
        )}
      </Button>
      <Button
        type="button"
        requires="edit"
        variant="neutral"
        size="sm"
        allowedTitle={warehouseFiltered ? FILTERED_COLLI_TITLE : copy.colliTitle}
        disabled={disabled || colliCount === 0}
        onClick={handled(onResolveColli)}
      >
        <Warning />
        {copy.colliLabel}
        {colliCount > 0 && (
          <Badge size="xs" variant="yellow">
            {colliCount}
          </Badge>
        )}
      </Button>
    </>
  )
}

type SelectionProps = {
  moqLockedReason?: string
  /** Short secondary-MOQ groups only partly selected, which Resolve MOQ skips. */
  partialGroupCount: number
  /** The group variable's name, e.g. "variant". */
  groupName: string
  onResolveMoq: () => void
  onResolveColli: () => void
}

/** Commandbar buttons resolving MOQ / colli size violations on the selected rows. */
export function ResolveViolationsSelectionButtons({
  moqLockedReason,
  partialGroupCount,
  groupName,
  onResolveMoq,
  onResolveColli,
}: SelectionProps) {
  return (
    <>
      <div className="flex items-center gap-1">
        <Button
          requires="edit"
          variant="neutral"
          size="sm"
          disabled={!!moqLockedReason}
          allowedTitle={moqLockedReason}
          onClick={onResolveMoq}
        >
          Resolve MOQ violations
        </Button>
        {partialGroupCount > 0 && (
          <Tooltip title={partiallySelectedGroupsMessage(partialGroupCount, groupName)}>
            <WarningCircle
              size={16}
              weight="fill"
              className="text-fg-warning-icon shrink-0"
              aria-label={`Some ${groupName}s are only partly selected`}
            />
          </Tooltip>
        )}
      </div>
      <Button requires="edit" variant="neutral" size="sm" onClick={onResolveColli}>
        Resolve colli size violations
      </Button>
    </>
  )
}
